import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useServiceSelection } from '../../../../hooks/useServiceSelection';
import { useBookingStore } from '../../../../store/bookingStore';
import ServiceSelectionView from './ServiceSelectionView';
import NavigationButtons from '../../shared/NavigationButtons';

// Funções reais para buscar dados do Firestore
const fetchServices = async (salonId) => {
  try {
    const servicesRef = collection(db, `salons/${salonId}/services`);
    const servicesQuery = query(servicesRef, orderBy('ordem', 'asc'));
    const snapshot = await getDocs(servicesQuery);
    
    if (snapshot.empty) {
      console.log('Nenhum serviço encontrado para o salão:', salonId);
      return [];
    }
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erro ao buscar serviços:', error);
    throw error;
  }
};

const fetchCategories = async (salonId) => {
  try {
    const categoriesRef = collection(db, `salons/${salonId}/categories`);
    const categoriesQuery = query(categoriesRef, orderBy('ordem', 'asc'));
    const snapshot = await getDocs(categoriesQuery);
    
    if (snapshot.empty) {
      console.log('Nenhuma categoria encontrada para o salão:', salonId);
      return [];
    }
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erro ao buscar categorias:', error);
    throw error;
  }
};

/**
 * Componente container para a etapa de seleção de serviços
 * 
 * @param {Object} props - Propriedades do componente
 * @param {string} props.salonId - ID do salão
 * @param {Object} props.salonConfig - Configurações do salão
 * @param {Function} props.onContinue - Função chamada ao continuar
 * @param {boolean} props.isAdmin - Indica se está no modo admin
 * @param {Array} props.preselectedServices - Serviços pré-selecionados (opcional)
 * @param {boolean} props.hasCustomPrices - Indica se existem profissionais com preços personalizados
 */
const ServiceSelection = ({ 
  salonId, 
  salonConfig = {}, 
  onContinue,
  isAdmin = false,
  preselectedServices = null,
  customLayout = 'standard',
  hasCustomPrices = false
}) => {
  // Buscar serviços
  const { data: services, isLoading: servicesLoading, error: servicesError } = useQuery({
    queryKey: ['services', salonId],
    queryFn: () => fetchServices(salonId),
    staleTime: 5 * 60 * 1000, // 5 minutos
    enabled: !!salonId, // Só buscar se tiver salonId
    retry: 2,
    onError: (error) => console.error('Erro ao buscar serviços:', error)
  });
  
  // Buscar categorias
  const { data: categories, isLoading: categoriesLoading, error: categoriesError } = useQuery({
    queryKey: ['categories', salonId],
    queryFn: () => fetchCategories(salonId),
    staleTime: 5 * 60 * 1000, // 5 minutos
    enabled: !!salonId, // Só buscar se tiver salonId
    retry: 2,
    onError: (error) => console.error('Erro ao buscar categorias:', error)
  });
  
  // Hook de seleção de serviços
  const { 
    selectedServices, 
    toggleService,
    selectServices,
    servicesCount
  } = useServiceSelection(salonId);
  
  // Form context do React Hook Form
  const { trigger } = useFormContext();
  
  // Pré-selecionar serviços se fornecidos
  useEffect(() => {
    if (preselectedServices && Array.isArray(preselectedServices) && preselectedServices.length > 0) {
      selectServices(preselectedServices);
    }
  }, [preselectedServices, selectServices]);
  
  // Estado de carregamento
  const isLoading = servicesLoading || categoriesLoading;
  const hasError = servicesError || categoriesError;
  
  // Verificar se o botão continuar deve estar desabilitado
  const continueDisabled = servicesCount === 0;
  
  // Função para avançar para a próxima etapa
  const handleContinue = async () => {
    // Validar o formulário antes de avançar
    const isValid = await trigger('services');
    if (isValid) {
      onContinue();
    }
  };
  
  // Lógica específica para o modo admin, se necessário
  const handleServiceToggle = (service) => {
    if (isAdmin && service.adminOnly) {
      // Lógica especial para serviços só disponíveis no admin
      console.log('Serviço apenas para admin:', service);
    }
    toggleService(service);
  };
  
  if (hasError) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <p className="text-red-500 text-center py-4">
          Erro ao carregar serviços. Tente novamente mais tarde.
        </p>
        <NavigationButtons 
          continueDisabled={true}
          continueLabel="Continuar"
          showBack={false}
          primaryColor={salonConfig?.primaryColor}
        />
      </div>
    );
  }
  
  return (
    <ServiceSelectionView 
      categories={categories || []}
      services={services || []}
      selectedServices={selectedServices}
      onServiceToggle={handleServiceToggle}
      isLoading={isLoading}
      primaryColor={salonConfig?.primaryColor}
      onContinue={handleContinue}
      continueDisabled={continueDisabled}
      NavigationButtons={NavigationButtons}
      isAdmin={isAdmin}
      hasCustomPrices={hasCustomPrices}
      salonConfig={salonConfig}
    />
  );
};

export default ServiceSelection; 